// Mention tokens — the one format both sides agree on.
//
// A question is stored as plain text in which each mentioned system appears as
// `@[Display Name](slug)`. The token is readable by the model (it sees
// "@[P21 Cloud](p21-cloud)"), unambiguous to parse (names may contain spaces),
// and survives a system rename because the slug is authoritative.
//
// Mentions are optional. The server reads them from the question text itself,
// so the transcript is the single source of truth for "which systems did this
// question tag" — tagged systems are answered from first.

export const MAX_MENTIONS = 2

const TOKEN_RE = /@\[([^\]\n]{1,80})\]\(([a-z0-9][a-z0-9-]{0,62})\)/g
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/

export function isSlug(value) {
  return typeof value === 'string' && SLUG_RE.test(value)
}

export function slugify(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
}

function cleanLabel(label) {
  return String(label ?? '')
    .replace(/[[\]\n]/g, '')
    .trim()
}

/** `@[P21 Cloud](p21-cloud)` */
export function encodeMention({ slug, name }) {
  if (!isSlug(slug)) throw new Error(`Invalid system slug: ${slug}`)
  return `@[${cleanLabel(name) || slug}](${slug})`
}

/**
 * Split a stored question into text and mention segments.
 * @returns {{ segments: Array<{type:'text', value:string} | {type:'mention', slug:string, label:string}>,
 *            mentions: string[], plain: string }}
 */
export function parseQuestion(text) {
  const source = String(text ?? '')
  const segments = []
  const mentions = []
  let last = 0
  for (const match of source.matchAll(TOKEN_RE)) {
    if (match.index > last) segments.push({ type: 'text', value: source.slice(last, match.index) })
    const [, label, slug] = match
    segments.push({ type: 'mention', slug, label })
    if (!mentions.includes(slug)) mentions.push(slug)
    last = match.index + match[0].length
  }
  if (last < source.length) segments.push({ type: 'text', value: source.slice(last) })
  return { segments, mentions, plain: stripMentions(source) }
}

export function mentionsOf(text) {
  return parseQuestion(text).mentions
}

/** The question with mention tokens removed and whitespace collapsed. */
export function stripMentions(text) {
  return String(text ?? '')
    .replace(TOKEN_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Mentions as plain system names: "@[P21 Cloud](p21-cloud) API" → "P21 Cloud API".
 * A stray "@" typed before a word is dropped too (email addresses are kept).
 */
export function mentionsToNames(text) {
  return parseQuestion(text)
    .segments.map((seg) => (seg.type === 'mention' ? seg.label : seg.value))
    .join('')
    .replace(/(^|\s)@(?=[A-Za-z])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/** A default conversation title derived from the first question. */
export function titleFromQuestion(text, max = 80) {
  const plain = stripMentions(text).replace(/^[@\s]+|[@\s]+$/g, '')
  if (!plain) return 'New question'
  if (plain.length <= max) return plain
  const cut = plain.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

/**
 * Validate a mention list against the catalog. An empty list is valid: the
 * question is then answered from every system.
 * @param {string[]} slugs
 * @param {Map<string, {status:string, name:string}>} catalog
 * @returns {{ ok: true, slugs: string[] } | { ok: false, code: string, message: string }}
 */
export function validateMentions(slugs, catalog) {
  const unique = [...new Set(slugs)]
  if (unique.length > MAX_MENTIONS) {
    return {
      ok: false,
      code: 'too_many_mentions',
      message: `Mention at most ${MAX_MENTIONS} systems in one question.`,
    }
  }
  for (const slug of unique) {
    const system = catalog.get(slug)
    if (!system) return { ok: false, code: 'unknown_system', message: `Unknown system “${slug}”.` }
    if (system.status === 'none') {
      return {
        ok: false,
        code: 'no_skill',
        message: `${system.name} has no skill yet — add one in the System library.`,
      }
    }
  }
  return { ok: true, slugs: unique }
}
